import React, { useCallback, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { MenuItem, Select, Stack } from "@mui/material";

const AddNewFields = ({
    title = undefined,
    keyLabel = undefined,
    valueLabel = undefined,
    sendDataToParent = undefined,
    error,
    fieldList,
    setFieldList,
    showTypeSelect = false,
    renderRequired = true,
    showAddButton = true,
    showRemoveButton = true,
}) => {
    const fieldTypes = useSelector(
        (state) => state.leadAllSourcesState.sourceApiTypes.data?.field_types,
    );
    const language = useSelector((state) => state.languagesState.languages);

    const handleFieldChange = (e, index) => {
        const { name, value } = e.target;

        const list = [...fieldList];
        list[index][name] = value;
        setFieldList(list);
    };
    const checkedHandler = (e, index) => {
        const { name, checked } = e.target;
        let list = [...fieldList];
        list[index][name] = checked;
        setFieldList(list);
    };
    useEffect(() => {
        sendDataToParent && sendDataToParent(fieldList);
    }, [fieldList]);

    const handleFieldRemove = useCallback(
        (index) => {
            const list = [...fieldList];
            list.splice(index, 1);
            setFieldList(list);
        },
        [fieldList, setFieldList],
    );

    const handleFieldAdd = useCallback(() => {
        setFieldList([...fieldList, { field: "" }]);
    }, [fieldList, setFieldList]);

    return (
        <div className="lead-service-field-area">
            <div className="lead-service-field-header">
                <div className="left">
                    <span>{title ?? language?.lead?.field ?? "Field"}</span>
                </div>
                {renderRequired && (
                    <div className="right">
                        <span>{language?.lead?.required ?? "Required"}</span>
                    </div>
                )}
            </div>
            <div className="lead-service-field-content">
                {((fieldTypes &&
                    Object.keys(fieldTypes)?.length &&
                    showTypeSelect) ||
                    !showTypeSelect) &&
                    fieldList.map((singleField, index) => {
                        // Check errors separately for field name and type
                        const hasFieldNameError =
                            error &&
                            error.fieldName &&
                            Array.isArray(error.fieldName) &&
                            error.fieldName.includes(index);
                        const hasFieldTypeError =
                            error &&
                            error.fieldType &&
                            Array.isArray(error.fieldType) &&
                            error.fieldType.includes(index);
                        return (
                            <React.Fragment key={index}>
                                <div className="lead-service-field-item mb-3">
                                    <div className="left">
                                        {showRemoveButton && (
                                            <div className="lead-service-field-item-remove">
                                                {fieldList.length !== 1 && (
                                                    <a
                                                        onClick={() =>
                                                            handleFieldRemove(
                                                                index,
                                                            )
                                                        }
                                                    >
                                                        <CancelIcon></CancelIcon>
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        <Form.Group
                                            className="w-full"
                                            controlId="exampleForm.ControlInput1"
                                        >
                                            <Stack direction="row" gap={2}>
                                                <FormControl
                                                    style={{ width: "100%" }}
                                                    error={hasFieldNameError}
                                                >
                                                    <TextField
                                                        label={
                                                            keyLabel ?? `Field`
                                                        }
                                                        variant="outlined"
                                                        onChange={(e) =>
                                                            handleFieldChange(
                                                                e,
                                                                index,
                                                            )
                                                        }
                                                        value={
                                                            singleField.field
                                                        }
                                                        error={
                                                            hasFieldNameError
                                                        }
                                                        name="field"
                                                        size="small"
                                                        className="text-field-ui-workaround"
                                                    />
                                                    {hasFieldNameError && (
                                                        <FormHelperText>
                                                            {error?.code ??
                                                                language?.this_field_is_required ??
                                                                "This field is required"}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                                {valueLabel && (
                                                    <FormControl
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                        error={
                                                            hasFieldNameError
                                                        }
                                                    >
                                                        <TextField
                                                            label={
                                                                valueLabel ??
                                                                `Value`
                                                            }
                                                            variant="outlined"
                                                            onChange={(e) =>
                                                                handleFieldChange(
                                                                    e,
                                                                    index,
                                                                )
                                                            }
                                                            value={
                                                                singleField.value
                                                            }
                                                            error={
                                                                hasFieldNameError
                                                            }
                                                            name="value"
                                                            size="small"
                                                            className="text-field-ui-workaround"
                                                        />
                                                        {hasFieldNameError && (
                                                            <FormHelperText>
                                                                {error?.code ??
                                                                    language?.this_field_is_required ??
                                                                    "This field is required"}
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                                {showTypeSelect && (
                                                    <FormControl
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                        error={
                                                            hasFieldTypeError
                                                        }
                                                        className="form-control-bg-white"
                                                    >
                                                        <Select
                                                            size="small"
                                                            label={`Type`}
                                                            name="type"
                                                            value={
                                                                singleField?.type ??
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                handleFieldChange(
                                                                    e,
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            {fieldTypes &&
                                                                Object.keys(
                                                                    fieldTypes,
                                                                )?.map(
                                                                    (
                                                                        fieldKey,
                                                                    ) => (
                                                                        <MenuItem
                                                                            value={
                                                                                fieldKey
                                                                            }
                                                                            key={
                                                                                fieldKey
                                                                            }
                                                                        >
                                                                            {
                                                                                fieldTypes[
                                                                                    fieldKey
                                                                                ]
                                                                            }
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                        </Select>
                                                        {hasFieldTypeError && (
                                                            <FormHelperText>
                                                                {error?.code ??
                                                                    language?.this_field_is_required ??
                                                                    "This field is required"}
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                            </Stack>
                                        </Form.Group>
                                    </div>
                                    {renderRequired && (
                                        <div className="right">
                                            <Switch
                                                name="required"
                                                onChange={(e) =>
                                                    checkedHandler(e, index)
                                                }
                                                checked={singleField.required}
                                                size="small"
                                            />
                                        </div>
                                    )}
                                </div>
                                {showAddButton &&
                                    fieldList.length - 1 === index &&
                                    fieldList.length < 5000 && (
                                        <div
                                            className="lead-service-field-item add-item"
                                            onClick={handleFieldAdd}
                                        >
                                            <div className="left justify-content-start">
                                                <div className="lead-service-field-item-remove">
                                                    <AddCircleIcon></AddCircleIcon>
                                                </div>
                                                <span>
                                                    {language?.lead
                                                        ?.add_more_field ??
                                                        "ADD MORE FIELD"}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                            </React.Fragment>
                        );
                    })}
            </div>
        </div>
    );
};

export default AddNewFields;
